/**
 * Projects API Service (Legacy Compatibility)
 * Delegates to analysisRunsApi for backward compatibility
 */

import { analysisRunsApi } from './analysisRunsApi';
import { Project, ProjectCreate, ProjectUpdate } from '../../types/analysisRun';
import { CreateProjectData } from '../../types';

class ProjectsAPI {
  /**
   * Create a new project (legacy - delegates to analysisRunsApi)
   * Accepts legacy CreateProjectData which may not have matter_id
   */
  async createProject(projectData: CreateProjectData | ProjectCreate): Promise<Project> {
    // Convert legacy CreateProjectData to AnalysisRunCreate
    const analysisRunData: ProjectCreate = {
      name: projectData.name,
      matter_id: 'matter_id' in projectData ? projectData.matter_id : '', // Use empty string as default for legacy compatibility
      workflow_type: projectData.workflow_type || undefined,
      workflow_config: undefined, // Will be set in configuration step
    };
    return analysisRunsApi.createAnalysisRun(analysisRunData);
  }

  /**
   * Create a project with workflow (legacy - delegates to analysisRunsApi)
   * Accepts legacy CreateProjectData which may not have matter_id
   */
  async createProjectWithWorkflow(projectData: CreateProjectData | ProjectCreate): Promise<Project> {
    // Convert legacy CreateProjectData to AnalysisRunCreate
    const analysisRunData: ProjectCreate = {
      name: projectData.name,
      matter_id: 'matter_id' in projectData ? projectData.matter_id : '', // Use empty string as default for legacy compatibility
      workflow_type: projectData.workflow_type || undefined,
      workflow_config: undefined, // Will be set in configuration step
    };
    return analysisRunsApi.createAnalysisRun(analysisRunData);
  }

  /**
   * Get all projects (legacy - delegates to analysisRunsApi)
   */
  async getProjects(): Promise<Project[]> {
    return analysisRunsApi.getAnalysisRuns();
  }

  /**
   * Get a specific project by ID (legacy - delegates to analysisRunsApi)
   */
  async getProject(projectId: string): Promise<Project> {
    return analysisRunsApi.getAnalysisRun(projectId);
  }

  /**
   * Update a project (legacy - delegates to analysisRunsApi)
   */
  async updateProject(projectId: string, updateData: Partial<Project> | ProjectUpdate): Promise<Project> {
    // Extract only the fields that are valid for AnalysisRunUpdate
    const analysisRunUpdate: ProjectUpdate = {};

    if ('name' in updateData && updateData.name !== undefined) {
      analysisRunUpdate.name = updateData.name;
    }
    if ('status' in updateData && updateData.status !== undefined) {
      analysisRunUpdate.status = updateData.status as ProjectUpdate['status'];
    }
    if ('workflow_type' in updateData && updateData.workflow_type !== undefined) {
      analysisRunUpdate.workflow_type = updateData.workflow_type;
    }
    if ('workflow_locked' in updateData && updateData.workflow_locked !== undefined) {
      analysisRunUpdate.workflow_locked = updateData.workflow_locked;
    }

    return analysisRunsApi.updateAnalysisRun(projectId, analysisRunUpdate);
  }

  /**
   * Delete a project (legacy - delegates to analysisRunsApi)
   */
  async deleteProject(projectId: string): Promise<void> {
    return analysisRunsApi.deleteAnalysisRun(projectId);
  }
}

export const projectsApi = new ProjectsAPI();
